import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X as XIcon, Sparkles, CreditCard, Zap, ShieldCheck, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { signupHrefForLocale } from '../../utils/signupLangHref';
import MarketingCTA from './MarketingCTA';

interface PricingFeature {
  label: string;
  included: boolean;
}

interface PricingSectionProps {
  subtitle?: string;
}

const FEATURE_KEYS = [
  'profileComplete',
  'bookingManagement',
  'petClientRecords',
  'smartCalendar',
  'onlinePaymentStripe',
  'messagingNotifications',
  'packagesCredits',
  'billing',
  'autoMileageFees',
  'prioritySupport',
] as const;

function useHoverLift() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(hover: hover)');
    const update = () => setOn(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return on;
}

export default function PricingSection({ subtitle }: PricingSectionProps) {
  const { t, i18n } = useTranslation('marketing');
  const ns = 'pricingSection';
  const [isAnnual, setIsAnnual] = useState(true);
  const hoverLift = useHoverLift();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const essentialFeatures = useMemo<PricingFeature[]>(
    () =>
      FEATURE_KEYS.map((key, index) => ({
        label: t(`${ns}.features.${key}`),
        included: index < 4,
      })),
    [t]
  );

  const premiumFeatures = useMemo<PricingFeature[]>(
    () =>
      FEATURE_KEYS.map((key) => ({
        label: t(`${ns}.features.${key}`),
        included: true,
      })),
    [t]
  );

  const handleCTA = () => {
    navigate(isAuthenticated ? '/app' : signupHrefForLocale(i18n.language));
  };

  return (
    <div style={{ position: 'relative' }}>
      <style>{`
        @media (max-width: 960px) {
          .pricing-grid-with-arrow {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      {/* Decorative background elements */}
      <div style={{ position: 'absolute', top: '-100px', left: '50%', transform: 'translateX(-50%)', width: '80%', height: '400px', background: 'radial-gradient(circle, rgba(165,118,92,0.05) 0%, transparent 70%)', zIndex: 0, pointerEvents: 'none' }} />

      <div style={{ textAlign: 'center', marginBottom: '80px', position: 'relative', zIndex: 1 }}>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            fontFamily: 'Plus Jakarta Sans',
            fontSize: 'clamp(40px, 6vw, 64px)',
            fontWeight: 900,
            color: '#1e293b',
            letterSpacing: '-0.05em',
            marginBottom: '24px',
            lineHeight: 1
          }}
        >
          {t(`${ns}.titlePrefix`)}{' '}
          <span style={{ fontFamily: 'Plus Jakarta Sans', fontStyle: 'normal', fontWeight: 700, color: '#A5765C' }}>
            {t(`${ns}.titleHighlight`)}
            <span style={{ display: 'inline-block', width: '0.2em', height: '0.2em', marginLeft: '0.12em', verticalAlign: 'baseline', background: '#e6a23c', borderRadius: '50%' }} aria-hidden="true" />
          </span>
        </motion.h2>
        <motion.p
          className="lp-pricing-subtitle"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          style={{
            fontFamily: 'Inter',
            fontSize: '19px',
            color: '#64748b',
            maxWidth: '600px',
            margin: '0 auto 48px',
            lineHeight: 1.6
          }}
        >
          {subtitle ?? t(`${ns}.subtitle`)}
        </motion.p>

        {/* Pricing Toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '12px' }}>
          <div
            style={{
              display: 'flex',
              padding: '6px',
              background: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '100px',
              border: '1px solid rgba(165,118,92,0.15)',
              boxShadow: '0 10px 25px rgba(165,118,92,0.05)',
              position: 'relative'
            }}
          >
            <button
              onClick={() => setIsAnnual(false)}
              style={{
                padding: '12px 28px',
                borderRadius: '100px',
                border: 'none',
                background: !isAnnual ? '#1e293b' : 'transparent',
                color: !isAnnual ? 'white' : '#64748b',
                fontWeight: 800,
                fontSize: '15px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                zIndex: 2
              }}
            >
              {t('common.pricing.monthly')}
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              style={{
                padding: '12px 28px',
                borderRadius: '100px',
                border: 'none',
                background: isAnnual ? '#1e293b' : 'transparent',
                color: isAnnual ? 'white' : '#64748b',
                fontWeight: 800,
                fontSize: '15px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {t('common.pricing.annual')}
            </button>
          </div>
        </div>
        <div style={{ height: '30px' }}>
          <AnimatePresence>
            {isAnnual && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                style={{ 
                  color: '#16a34a', 
                  fontSize: '13px', 
                  fontWeight: 800, 
                  background: '#dcfce7', 
                  padding: '4px 12px', 
                  borderRadius: '100px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                 {t(`${ns}.annualSavings`)}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '40px',
          maxWidth: '1100px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1
        }}
        className="pricing-grid-with-arrow"
      >
        {/* Essentiel */}
        <motion.div
          className="lp-pricing-card"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: '48px',
            padding: '64px',
            border: '1px solid rgba(165,118,92,0.1)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.4s ease'
          }}
          whileHover={hoverLift ? { y: -10, boxShadow: '0 40px 80px rgba(0,0,0,0.05)' } : undefined}
        >
          <div style={{ marginBottom: '40px' }}>
            <span style={{ color: '#64748b', fontWeight: 900, fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase', display: 'block', marginBottom: '16px' }}>
              {t(`${ns}.essentialTagline`)}
            </span>
            <h3 style={{ fontSize: '32px', fontWeight: 900, color: '#1e293b', marginBottom: '8px' }}>{t('common.pricing.essential')}</h3>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{ fontFamily: 'Plus Jakarta Sans', fontStyle: 'normal', fontSize: '64px', fontWeight: 800, color: '#1e293b' }}>{t('common.pricing.priceZero')}</span>
              <span style={{ fontSize: '18px', color: '#64748b', fontWeight: 600 }}>{t('common.pricing.perMonth')}</span>
            </div>
          </div>

          <MarketingCTA
            label={isAuthenticated ? t(`${ns}.myAccount`) : t('common.cta.startFreeMarketing')}
            variant="outline"
            size="lg"
            onClick={handleCTA}
            fullWidth
            style={{ borderRadius: '100px', padding: '16px', background: 'white' }}
          />

          <div style={{ marginTop: '48px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <span style={{ fontSize: '13px', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>{t(`${ns}.includedLabel`)}</span>
            {essentialFeatures.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '16px', opacity: f.included ? 1 : 0.4 }}>
                <div style={{ 
                  width: '22px', 
                  height: '22px', 
                  borderRadius: '50%', 
                  background: f.included ? '#f1f5f9' : 'transparent', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {f.included ? <Check size={14} color="#1e293b" strokeWidth={3} /> : <XIcon size={14} color="#94a3b8" />}
                </div>
                <span style={{ fontSize: '15px', color: '#475569', fontWeight: f.included ? 600 : 400 }}>{f.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Premium */}
        <motion.div
          className="lp-pricing-card"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          style={{
            background: 'white',
            borderRadius: '48px',
            padding: '64px',
            border: '2px solid #A5765C',
            boxShadow: '0 40px 100px rgba(165,118,92,0.12)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.4s ease'
          }}
          whileHover={hoverLift ? { y: -10, boxShadow: '0 60px 120px rgba(165,118,92,0.2)' } : undefined}
        >
          {/* Badge 'Most Popular' */}
          <div
            className="lp-pricing-badge"
            style={{ 
            position: 'absolute', 
            top: '32px', 
            right: '32px', 
            background: '#A5765C', 
            color: 'white', 
            padding: '8px 20px', 
            borderRadius: '100px', 
            fontSize: '11px', 
            fontWeight: 900, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            boxShadow: '0 10px 20px rgba(165,118,92,0.2)'
          }}>
            <Sparkles size={14} fill="white" />
            {t(`${ns}.recommended`)}
          </div>

          <div style={{ marginBottom: '40px' }}>
            <span style={{ color: '#A5765C', fontWeight: 900, fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase', display: 'block', marginBottom: '16px' }}>
              {t(`${ns}.premiumTagline`)}
            </span>
            <h3 style={{ fontSize: '32px', fontWeight: 900, color: '#1e293b', marginBottom: '8px' }}>{t('common.pricing.premium')}</h3>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{ fontFamily: 'Plus Jakarta Sans', fontStyle: 'normal', fontSize: '64px', fontWeight: 800, color: '#1e293b' }}>
                {isAnnual ? t('common.pricing.priceAnnual') : t('common.pricing.priceMonthly')}
              </span>
              <span style={{ fontSize: '18px', color: '#64748b', fontWeight: 600 }}>{t('common.pricing.perMonth')}</span>
            </div>
            <p style={{ fontSize: '14px', color: '#16a34a', fontWeight: 700, marginTop: '8px' }}>
              {t(`${ns}.freeTrial`)}
            </p>
          </div>

          <MarketingCTA
            label={t(`${ns}.startFreeTrial`)}
            variant="primary"
            size="lg"
            onClick={handleCTA}
            fullWidth
            style={{ borderRadius: '100px', padding: '16px', background: '#1e293b', boxShadow: '0 15px 35px rgba(30,41,59,0.3)' }}
          />

          <div style={{ marginTop: '48px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <span style={{ fontSize: '13px', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>{t(`${ns}.includedPlusLabel`)}</span>
            {premiumFeatures.map((f, i) => {
              // Only highlight the 'extra' features
              const isExtra = i >= 5;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '22px', 
                    height: '22px', 
                    borderRadius: '50%', 
                    background: isExtra ? '#A5765C' : '#f1f5f9', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Check size={14} color={isExtra ? "white" : "#1e293b"} strokeWidth={3} />
                  </div>
                  <span style={{ fontSize: '15px', color: '#1e293b', fontWeight: isExtra ? 800 : 500 }}>{f.label}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Ligne de réassurance sous les cartes (même langage que les trust badges) */}
      <div
        className="lp-pricing-reassurance"
        style={{ 
        marginTop: '48px', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        gap: '40px', 
        flexWrap: 'wrap',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: '#64748b', opacity: 0.85 }}>
          <ShieldCheck size={18} /> {t(`${ns}.noCommitment`)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: '#64748b', opacity: 0.85 }}>
          <CreditCard size={18} /> {t(`${ns}.securePayment`)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: '#64748b', opacity: 0.85 }}>
          <Zap size={18} /> {t(`${ns}.quickSetup`)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: '#64748b' }}>
          <ArrowRight size={18} />
          <span>{t(`${ns}.upgradePrefix`)} <span style={{ color: '#A5765C' }}>{t(`${ns}.upgradeHighlight`)}</span></span>
        </div>
      </div>
    </div>
  );
}
